import type { TargetView } from '../../shared/api';
import { BUILTIN_BY_KEY, formatAmount, type NutrientDef, type NutrientMap } from '../../shared/nutrients';

export function kcal(n: number | undefined | null): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

export function grams(n: number | undefined | null, decimals = 0): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  return `${n.toLocaleString('en-US', { maximumFractionDigits: decimals })} g`;
}

export function amount(value: number | undefined, def: NutrientDef | undefined): string {
  return formatAmount(value, def ?? { decimals: 0 });
}

export function unitLabel(def: NutrientDef | undefined): string {
  if (!def) return '';
  return def.unit === 'kcal' ? 'kcal' : def.unit;
}

export function defFor(key: string, defs: Map<string, NutrientDef>): NutrientDef {
  return (
    defs.get(key) ??
    BUILTIN_BY_KEY.get(key) ?? { key, name: key, unit: 'g', group: 'custom', direction: 'target', decimals: 1 }
  );
}

export const MACRO_COLORS: Record<string, string> = {
  protein: 'var(--protein)',
  carbs: 'var(--carbs)',
  fat: 'var(--fat)',
  fiber: 'var(--fiber)',
};

export function colorFor(key: string): string {
  return MACRO_COLORS[key] ?? 'var(--extra)';
}

/** Progress toward a target, interpreted by direction. */
export function progress(target: TargetView, eaten: number): { pct: number; state: 'ok' | 'near' | 'over' | 'under' } {
  if (!target.amount || target.amount <= 0) return { pct: 0, state: 'ok' };
  const pct = eaten / target.amount;
  if (target.direction === 'max') return { pct, state: pct > 1 ? 'over' : pct > 0.9 ? 'near' : 'ok' };
  if (target.direction === 'min') return { pct, state: pct >= 1 ? 'ok' : 'under' };
  return { pct, state: pct > 1.1 ? 'over' : 'ok' };
}

export function directionWord(direction: TargetView['direction']): string {
  return direction === 'min' ? 'at least' : direction === 'max' ? 'at most' : 'aim for';
}

export function macroLine(n: NutrientMap): string {
  return `${kcal(n.kcal)} kcal · ${Math.round(n.protein ?? 0)} P · ${Math.round(n.carbs ?? 0)} C · ${Math.round(n.fat ?? 0)} F`;
}

export function pluralize(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
